import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';

import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';
import { useCompanyFeeSchedule } from '../../hooks/useFeeSchedules';
import { calculatePreview, formatCents } from '../../utils/feePreview';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  access_type: string;
  access_value: number | null;
  billing_cycle: string;
}

interface OwnerEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: {
    user_id: string;
    company_id: string;
    first_name?: string | null;
    last_name?: string | null;
  };
  onSuccess?: () => void;
}

type Step = 'select' | 'confirm';

export function OwnerEnrollmentModal({ isOpen, onClose, student, onSuccess: _onSuccess }: OwnerEnrollmentModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionPlan | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: feeSchedule } = useCompanyFeeSchedule(isOpen ? student.company_id : null);

  // Load available subscriptions when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset state
    setStep('select');
    setSelectedSubscription(null);
    setError(null);

    const loadSubscriptions = async () => {
      setLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error: subError } = await (supabase as any)
          .from('company_subscriptions')
          .select('id, name, price, access_type, access_value, billing_cycle')
          .eq('company_id', student.company_id)
          .order('name', { ascending: true });

        if (subError) throw subError;
        setSubscriptions(data || []);
      } catch (err) {
        console.error('Error loading subscriptions:', err);
        setError('Failed to load subscription plans.');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptions();
  }, [isOpen, student.company_id]);

  // Calculate proration and invoice preview
  const invoicePreview = useMemo(() => {
    if (!selectedSubscription || !feeSchedule?.items) return null;

    const now = new Date();
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const daysRemaining = daysInMonth - now.getDate() + 1;

    const basePriceCents = Math.round(selectedSubscription.price * 100);
    const isProrated = daysRemaining < daysInMonth;
    const proratedBaseCents = isProrated
      ? Math.round((basePriceCents * daysRemaining) / daysInMonth)
      : basePriceCents;

    const preview = calculatePreview(feeSchedule.items, proratedBaseCents);

    return {
      basePriceCents,
      proratedBaseCents,
      isProrated,
      daysRemaining,
      daysInMonth,
      ...preview,
    };
  }, [selectedSubscription, feeSchedule]);

  const handleSelectSubscription = (sub: SubscriptionPlan) => {
    setSelectedSubscription(sub);
    setStep('confirm');
  };

  const handleConfirmEnrollment = async () => {
    if (!selectedSubscription) return;

    setSubmitting(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const { data, error: invokeError } = await supabase.functions.invoke('create-owner-enrollment-checkout', {
        body: {
          studentUserId: student.user_id,
          subscriptionId: selectedSubscription.id,
          companyId: student.company_id,
        },
      });

      if (invokeError) throw invokeError;
      if (!data?.success) throw new Error(data?.error || 'Failed to create checkout session');

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch (err: any) {
      console.error('Error creating enrollment checkout:', err);
      setError(err?.message || 'Failed to start enrollment. Please try again.');
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const studentName = `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Student';

  const formatBillingCycle = (cycle: string) => {
    switch (cycle) {
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      case 'annual': return 'Annual';
      case 'biyearly': return 'Bi-yearly';
      case 'one_time': return 'One-time';
      default: return cycle || 'Monthly';
    }
  };

  const formatAccess = (sub: SubscriptionPlan) => {
    if (sub.access_type === 'unlimited') return 'Unlimited';
    return `${sub.access_value} ${sub.access_type}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[#EBFFEE]">
              {step === 'select' ? 'Select Subscription Plan' : 'Confirm Enrollment'}
            </h3>
            <p className="text-sm text-[#A9A3A3] mt-1">
              Enrolling: {studentName}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-1 rounded ${step === 'select' || step === 'confirm' ? 'bg-[#9B1421]' : 'bg-[#504A4A]'}`} />
          <div className={`flex-1 h-1 rounded ${step === 'confirm' ? 'bg-[#9B1421]' : 'bg-[#504A4A]'}`} />
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Step 1: Select subscription */}
        {step === 'select' && (
          <>
            {loading ? (
              <p className="text-[#A9A3A3] text-center py-8">Loading subscriptions...</p>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#A9A3A3]">No subscriptions available.</p>
                <p className="text-sm text-[#A9A3A3] mt-2">Create a subscription plan first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => handleSelectSubscription(sub)}
                    className="w-full bg-[#363232] hover:bg-[#504A4A] border border-[#504A4A] rounded-lg p-4 text-left transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="text-[#EBFFEE] font-semibold">{sub.name}</p>
                        <p className="text-[#A9A3A3] text-sm mt-1">
                          ${Number(sub.price).toFixed(2)} / {formatBillingCycle(sub.billing_cycle)}
                        </p>
                        <p className="text-[#A9A3A3] text-xs mt-1">{formatAccess(sub)}</p>
                      </div>
                      <span className="text-[#A9A3A3]">&gt;</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Step 2: Confirm */}
        {step === 'confirm' && selectedSubscription && (
          <div className="space-y-4">
            {/* Enrollment details */}
            <div className="bg-[#363232] rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Plan</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{selectedSubscription.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Start Date</span>
                <span className="text-[#EBFFEE] text-sm font-medium">{new Date().toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#A9A3A3] text-sm">Price</span>
                <span className="text-[#EBFFEE] text-sm font-medium">
                  ${Number(selectedSubscription.price).toFixed(2)} / {formatBillingCycle(selectedSubscription.billing_cycle)}
                </span>
              </div>
            </div>

            {/* Invoice preview */}
            {invoicePreview && (
              <div>
                <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                  Invoice Preview
                </label>
                <div className="space-y-1.5">
                  {invoicePreview.isProrated ? (
                    <>
                      <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                        <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                        <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.basePriceCents)}</span>
                      </div>
                      <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                        <span className="text-[#A9A3A3] text-sm">
                          Prorated ({invoicePreview.daysRemaining}/{invoicePreview.daysInMonth} days)
                        </span>
                        <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.proratedBaseCents)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                      <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                      <span className="text-[#EBFFEE] text-sm">{formatCents(invoicePreview.proratedBaseCents)}</span>
                    </div>
                  )}
                  {invoicePreview.lineItems
                    .filter(li => li.chargeEffect === 'add' && li.amountCents > 0)
                    .map((li, idx) => (
                    <div key={idx} className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                      <span className="text-[#A9A3A3] text-sm">{li.heading}</span>
                      <span className="text-[#EBFFEE] text-sm">+{formatCents(li.amountCents)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center px-3 py-2 bg-[#504A4A] rounded-md font-semibold">
                    <span className="text-[#EBFFEE] text-sm">Due today</span>
                    <span className="text-red-400 text-sm">{formatCents(invoicePreview.payerCents)}</span>
                  </div>
                </div>
              </div>
            )}

            <p className="text-[#A9A3A3] text-xs">
              You will be redirected to Stripe to enter the student's payment details.
            </p>

            <div className="flex gap-3">
              <Button
                onClick={() => { setStep('select'); setError(null); }}
                disabled={submitting}
                className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              >
                Back
              </Button>
              <Button
                onClick={handleConfirmEnrollment}
                disabled={submitting}
                className="flex-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Redirecting...
                  </span>
                ) : (
                  'Proceed to Payment'
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
